import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface ParteTrabajo {
  id: string;
  cliente_id: string | null;
  cliente_libre?: string | null;
  tecnico_id: string;
  fecha_trabajo: string;
  hora_inicio: string;
  hora_fin?: string | null;
  duracion_total?: unknown;
  descripcion_trabajo: string;
  tipo_intervencion: 'revision_sistema_seguridad' | 'averias' | 'auditoria' | 'entrega_materiales' | 'otros';
  orden_trabajo_id?: string | null;
  nota_interna?: string | null;
  firma_tecnico?: string | null;
  firma_cliente?: string | null;
  estado: 'borrador' | 'finalizado' | 'facturado';
  numero_parte?: string | null;
  tiene_desplazamiento?: boolean;
  forma_pago?: 'efectivo' | 'transferencia' | 'tarjeta' | null;
  importe_sin_iva?: number | null;
  iva_porcentaje?: number;
  importe_con_iva?: number | null;
  resultado_intervencion?: 'resuelto' | 'no_resuelto' | 'no_realizado' | null;
  requiere_nueva_visita?: boolean;
  motivo_no_resuelto?: string | null;
  materiales_necesarios?: string | null;
  direccion_intervencion?: string | null;
  codigo_postal_intervencion?: string | null;
  poblacion_intervencion?: string | null;
  created_at: string;
  updated_at: string;
}

interface PartesFilters {
  search: string;
  estado: string;
  tipoIntervencion: string;
  fechaDesde: string;
  fechaHasta: string;
  tecnicoId: string;
  clienteId: string;
  conOT: string;
}

interface PartesTrabajoContextType {
  partes: ParteTrabajo[];
  loading: boolean;
  createParte: (parte: Omit<ParteTrabajo, 'id' | 'created_at' | 'updated_at'>) => Promise<ParteTrabajo>;
  updateParte: (id: string, updates: Partial<ParteTrabajo>) => Promise<void>;
  deleteParte: (id: string) => Promise<void>;
  getParteById: (id: string) => ParteTrabajo | undefined;
  getPartesByOrdenId: (ordenId: string) => ParteTrabajo[];
  getPartesWithOrdenData: () => (ParteTrabajo & { orden_trabajo?: any })[];
  getFilteredPartes: (filters: PartesFilters) => (ParteTrabajo & { orden_trabajo?: any; cliente?: any; tecnico?: any })[];
  getEstadoCounts: () => { borrador: number; finalizado: number; facturado: number };
  refreshPartes: () => Promise<void>;
}

const PartesTrabajoContext = createContext<PartesTrabajoContextType | undefined>(undefined);

export const usePartesTrabajoContext = () => {
  const context = useContext(PartesTrabajoContext);
  if (!context) {
    throw new Error('usePartesTrabajoContext must be used within a PartesTrabajoProvider');
  }
  return context;
};

export const PartesTrabajoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [partes, setPartes] = useState<ParteTrabajo[]>([]);
  const [partesWithOrden, setPartesWithOrden] = useState<(ParteTrabajo & { orden_trabajo?: any; cliente?: any; tecnico?: any })[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, perfil } = useAuth();
  const { toast } = useToast();

  console.log('PartesTrabajoProvider: Initializing with user:', user?.id, 'loading:', loading);

  const loadPartes = async () => {
    if (!user) {
      console.log('PartesTrabajoProvider: No user, skipping load');
      return;
    }
    
    console.log('PartesTrabajoProvider: Starting to load partes for user:', user.id);
    
    try {
      setLoading(true);
      console.log('PartesTrabajoProvider: Executing Supabase query...');
      
      // First, try a simple query to verify basic access
      const { data, error } = await supabase
        .from('partes_trabajo')
        .select(`
          *,
          ordenes_trabajo!fk_partes_trabajo_orden(id, numero_ot, titulo),
          clientes!fk_partes_trabajo_cliente(id, nombre),
          tecnico:perfiles!fk_partes_trabajo_tecnico(user_id, nombre)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('PartesTrabajoProvider: Supabase error details:', JSON.stringify(error, null, 2));
        console.error('PartesTrabajoProvider: Error code:', error.code);
        console.error('PartesTrabajoProvider: Error message:', error.message);
        console.error('PartesTrabajoProvider: Error hint:', error.hint);
        throw error;
      }
      
      console.log('PartesTrabajoProvider: Query successful, loaded', data?.length || 0, 'partes');
      console.log('PartesTrabajoProvider: Sample data:', data?.[0] ? JSON.stringify(data[0], null, 2) : 'No data');
      
      setPartes((data || []) as ParteTrabajo[]);
      setPartesWithOrden((data || []) as (ParteTrabajo & { orden_trabajo?: any; cliente?: any; tecnico?: any })[]);
    } catch (error) {
      console.error('PartesTrabajoProvider: Full error details:', JSON.stringify(error, null, 2));
      toast({
        title: "Error",
        description: "No se pudieron cargar los partes de trabajo",
        variant: "destructive",
      });
    } finally {
      console.log('PartesTrabajoProvider: Load completed, setting loading to false');
      setLoading(false);
    }
  };

  const createParte = async (parte: Omit<ParteTrabajo, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const parteConEmpresa = {
        ...parte,
        empresa_id: perfil?.empresa_id,
      };
      const { data, error } = await supabase
        .from('partes_trabajo')
        .insert([parteConEmpresa as any])
        .select(`
          *,
          ordenes_trabajo!fk_partes_trabajo_orden(id, numero_ot, titulo),
          clientes!fk_partes_trabajo_cliente(id, nombre),
          tecnico:perfiles!fk_partes_trabajo_tecnico(user_id, nombre)
        `)
        .single();

      if (error) throw error;
      
      // Actualizar AMBOS estados para mantener sincronización
      setPartes(prev => [data as ParteTrabajo, ...prev]);
      setPartesWithOrden(prev => [data as (ParteTrabajo & { orden_trabajo?: any; cliente?: any; tecnico?: any }), ...prev]);
      
      // Show success message with outcome context
      let successMessage = "Parte de trabajo creado correctamente";
      if (parte.orden_trabajo_id) {
        if (parte.resultado_intervencion === 'resuelto') {
          successMessage += " - Orden de trabajo marcada como finalizada";
        } else if (parte.resultado_intervencion) {
          successMessage += " - Orden de trabajo actualizada";
        }
      }
      
      toast({
        title: "Éxito",
        description: successMessage,
      });
      
      return data as ParteTrabajo;
    } catch (error) {
      console.error('Error creating parte:', error);
      toast({
        title: "Error",
        description: "No se pudo crear el parte de trabajo",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateParte = async (id: string, updates: Partial<ParteTrabajo>) => {
    try {
      const { data, error } = await supabase
        .from('partes_trabajo')
        .update(updates as any)
        .eq('id', id)
        .select(`
          *,
          ordenes_trabajo!fk_partes_trabajo_orden(id, numero_ot, titulo),
          clientes!fk_partes_trabajo_cliente(id, nombre),
          tecnico:perfiles!fk_partes_trabajo_tecnico(user_id, nombre)
        `)
        .single();

      if (error) throw error;
      
      // Actualizar AMBOS estados para mantener sincronización
      setPartes(prev => prev.map(p => p.id === id ? (data as ParteTrabajo) : p));
      setPartesWithOrden(prev => prev.map(p => p.id === id ? (data as (ParteTrabajo & { orden_trabajo?: any; cliente?: any; tecnico?: any })) : p));
      
      toast({
        title: "Éxito",
        description: "Parte de trabajo actualizado correctamente",
      });
    } catch (error) {
      console.error('Error updating parte:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el parte de trabajo",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteParte = async (id: string) => {
    try {
      const { error } = await supabase
        .from('partes_trabajo')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      // Actualizar AMBOS estados para mantener sincronización
      setPartes(prev => prev.filter(p => p.id !== id));
      setPartesWithOrden(prev => prev.filter(p => p.id !== id));
      
      toast({
        title: "Éxito",
        description: "Parte de trabajo eliminado correctamente",
      });
    } catch (error) {
      console.error('Error deleting parte:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el parte de trabajo",
        variant: "destructive",
      });
      throw error;
    }
  };

  const getParteById = (id: string): ParteTrabajo | undefined => {
    return partes.find(p => p.id === id);
  };

  const getPartesByOrdenId = (ordenId: string): ParteTrabajo[] => {
    return partes.filter(p => p.orden_trabajo_id === ordenId);
  };

  const getPartesWithOrdenData = (): (ParteTrabajo & { orden_trabajo?: any; cliente?: any; tecnico?: any })[] => {
    return partesWithOrden;
  };

  const getFilteredPartes = (filters: PartesFilters): (ParteTrabajo & { orden_trabajo?: any; cliente?: any; tecnico?: any })[] => {
    let filtered = partesWithOrden;

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(parte => 
        parte.descripcion_trabajo?.toLowerCase().includes(searchTerm) ||
        parte.numero_parte?.toLowerCase().includes(searchTerm) ||
        parte.cliente?.nombre?.toLowerCase().includes(searchTerm) ||
        parte.cliente_libre?.toLowerCase().includes(searchTerm) ||
        parte.tecnico?.nombre?.toLowerCase().includes(searchTerm)
      );
    }

    // Estado filter
    if (filters.estado && filters.estado !== 'all') {
      filtered = filtered.filter(parte => parte.estado === filters.estado);
    }

    // Tipo intervención filter
    if (filters.tipoIntervencion && filters.tipoIntervencion !== 'all') {
      filtered = filtered.filter(parte => parte.tipo_intervencion === filters.tipoIntervencion);
    }

    // Date range filters
    if (filters.fechaDesde) {
      filtered = filtered.filter(parte => parte.fecha_trabajo >= filters.fechaDesde);
    }
    if (filters.fechaHasta) {
      filtered = filtered.filter(parte => parte.fecha_trabajo <= filters.fechaHasta);
    }

    // Técnico filter
    if (filters.tecnicoId && filters.tecnicoId !== 'all') {
      filtered = filtered.filter(parte => parte.tecnico_id === filters.tecnicoId);
    }

    // Cliente filter
    if (filters.clienteId && filters.clienteId !== 'all') {
      filtered = filtered.filter(parte => parte.cliente_id === filters.clienteId);
    }

    // OT relation filter
    if (filters.conOT === 'con_ot') {
      filtered = filtered.filter(parte => parte.orden_trabajo_id);
    } else if (filters.conOT === 'sin_ot') {
      filtered = filtered.filter(parte => !parte.orden_trabajo_id);
    }

    return filtered;
  };

  const getEstadoCounts = () => {
    const counts = { borrador: 0, finalizado: 0, facturado: 0 };
    partesWithOrden.forEach(parte => {
      if (parte.estado in counts) {
        counts[parte.estado as keyof typeof counts]++;
      }
    });
    return counts;
  };

  const refreshPartes = async () => {
    await loadPartes();
  };

  useEffect(() => {
    if (user) {
      loadPartes();
    }
  }, [user]);

  const value: PartesTrabajoContextType = {
    partes,
    loading,
    createParte,
    updateParte,
    deleteParte,
    getParteById,
    getPartesByOrdenId,
    getPartesWithOrdenData,
    getFilteredPartes,
    getEstadoCounts,
    refreshPartes,
  };

  return (
    <PartesTrabajoContext.Provider value={value}>
      {children}
    </PartesTrabajoContext.Provider>
  );
};